import { LayerField, LayerParams } from '../../types/map-layer';
import { useQuery } from '../../../../hooks/use-query';
import { useNavigate, useParams } from 'react-router';
import { Modes } from '../../../../types/page-modes';
import {
  Actions,
  Content as DialogContent,
  Dialog,
  Title
} from '../../../../components/dialog/dialog';
import { Label, TextInputLarge } from '../../../../components/forms/input';
import { Icons } from '../../../../components/icons/icons';
import usePageState from '../../../../hooks/use-page-state';
import { FieldValues, useForm } from 'react-hook-form';
import * as Button from '../../../../components/button/button';
import { Checkbox2 } from '../../../../components/forms/checkbox';
import { Select2 } from '../../../../components/forms/select';
import { useDeleteMutation, usePostMutation, usePutMutation } from '../../../../hooks/use-mutations';
import { Table } from '../../../../components/table/table';
import { ActionColumn, ManuallyAddedColumn } from '../../../../components/table/columns';
import { Pageable, usePagination } from '../../../../hooks/use-pagination';
import { ValueFormatter } from '../../../settings/types/formatters';
import React, { useMemo, useEffect } from 'react';
import { KeyValue } from '../../../../types/key-value';
import { alert } from '../../../../components/toast/toast';

export const FieldsList = (): JSX.Element => {
  const navigate = useNavigate();
  const [pageState, setPageState] = usePageState();
  const { register, handleSubmit, getValues } = useForm();
  const params = useParams<LayerParams>();
  const layerId = params.id as string;
  const fields = useQuery<LayerField[]>({ path: `/map-layers/${layerId}/fields` });
  const formatters = usePagination<Pageable<ValueFormatter>>('/formatter', { size: 9999 });
  const deleteMutation = useDeleteMutation({ cacheKey: `/map-layers/${layerId}/fields` });
  const postMutation = usePostMutation<LayerField>({ path: '/map-layers/fields', cacheKey: `/map-layers/${layerId}/fields` });
  const putMutation = usePutMutation<LayerField>({ path: `/map-layers/fields/${pageState.item?.id as string}`, cacheKey: `/map-layers/${layerId}/fields` });
  const convertPollingFieldsPostMutation = usePostMutation<LayerField[]>({
    path: `/map-layers/${layerId}/fields/convert`,
    cacheKey: `/map-layers/${layerId}/fields`
  });

  const formattersData = formatters.data?.content || [];
  const formatterMap = useMemo(() => {
    const map: KeyValue = {};
    formattersData.forEach((item): void => {
      map[item.id] = item.type as string;
    });
    return map;
  }, [formattersData]);

  useEffect(() => {
    if (convertPollingFieldsPostMutation.isSuccess) {
      alert.success('Fields converted successfully');
    }
  }, [convertPollingFieldsPostMutation.isLoading]);

  const onSubmit = (data: FieldValues): void => {
    pageState.item?.id ? putMutation.mutate({ ...data, layerId }) : postMutation.mutate({ ...data, layerId });
    setPageState(Modes.BROWSE);
  };

  const addField = (): void => {
    navigate(`/map-layers/${layerId}/fields/new/editor`);
  };

  const updateField = (item: LayerField): void => {
    navigate(`/map-layers/${layerId}/fields/${item?.id as string}/editor`);
  };

  const columns = [
    { accessor: 'fieldName', Header: 'Field Name' },
    { accessor: 'fieldAlias', Header: 'Field Alias' },
    { accessor: 'fieldType', Header: 'Field Type' },
    { accessor: 'displayWithAttributes', width: 200, Header: 'Display With Attributes', Cell: (item: any): JSX.Element => (
      <Checkbox2 name="" defaultChecked={Boolean(item.row.original?.displayWithAttributes)} readOnly />
    ) },
    { accessor: 'displayWithMapTips', width: 200, Header: 'Display with Map Tips', Cell: (item: any): JSX.Element => (
      <Checkbox2 name="" defaultChecked={Boolean(item.row.original?.displayWithMapTips)} readOnly />
    ) },
    { accessor: 'filter', Header: 'Filter', Cell: (item: any): JSX.Element => (
      <Checkbox2 name="" defaultChecked={Boolean(item.original?.row?.filter)} readOnly />
    ) },
    { accessor: 'formatterId', Header: 'Formatter', Cell: (item: any): JSX.Element => {
      return <span>{formatterMap[item.row.original.formatterId]}</span>;
    } },
    ManuallyAddedColumn(),
    {
      ...ActionColumn(), Cell: (item: any): JSX.Element => {
        const row = item.row.original as LayerField;
        if (!row.indexed) {
          return <div role="cell" className="body-cell new-td new-td-action" style={{ boxSizing: 'border-box', flex: '164 0 auto', minWidth: '0', width: '164px' }} />;
        }
        return ActionColumn({
          onDelete: (item: LayerField): void => setPageState(Modes.DELETE, item),
          onUpdate: updateField
        }).Cell(item);
      }
    }
  ];

  const convertPollingFields = (): void => {
    convertPollingFieldsPostMutation.mutate(undefined);
  };

  const onDeleteConfirm = (): void => {
    deleteMutation.mutate({ path: `/map-layers/fields/${pageState.item?.id as string}`  });
    setPageState(Modes.BROWSE);
  };

  const indexed = fields.data?.find(item => !item.indexed);

  return <div>
    <div className="d-flex justify-content-end mb-4">
      <Button.LightBlackSmall disabled={!fields.data || !indexed || convertPollingFieldsPostMutation.isLoading} onClick={convertPollingFields}>
        <Icons.Union />
        Convert Polling Fields
      </Button.LightBlackSmall>
      <Button.LightBlackSmall className="ms-2" onClick={addField}>
        <Icons.Add />
        Add Field
      </Button.LightBlackSmall>
    </div>
    <Table columns={columns} rows={fields.data as any || []} />
    {(pageState.mode === Modes.CREATE || pageState.mode === Modes.UPDATE) && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} className="large-dialog" backdropClose={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title>Add Field</Title>
          <DialogContent>
            <div className="mb-4">
              <Label name="key">Field Name</Label>
              <TextInputLarge {...register('fieldName')} placeholder="Field Name" />
            </div>
            <div className="mb-4">
              <Label name="name">Field Alias</Label>
              <TextInputLarge {...register('fieldAlias')} placeholder="Field Alias" />
            </div>
            <div className="mb-4">
              <Checkbox2 {...register('displayWithAttributes')} label="Display With Attributes" defaultChecked={getValues().displayWithAttributes} />
            </div>
            <div className="mb-4">
              <Checkbox2 {...register('displayWithMapTips')} label="Display With Map Tips" defaultChecked={getValues().displayWithMapTips} />
            </div>
            <div className="mb-4">
              <Label>Value Formatter Id</Label>
              <Select2 {...register('formatterId')}>
                <option value="">Select an option</option>
                {formatters.data?.content.map((formatter: ValueFormatter) => (
                  <option key={formatter.id} value={formatter.id}>{formatter.type}</option>
                ))}
              </Select2>
            </div>
            <div className="mb-4">
              <Label>Field Type</Label>
              <Select2 {...register('fieldType')}>
                <option value="">Select an option</option>
                {fieldTypes.map(fieldType => <option key={fieldType.value} value={fieldType.value}>{fieldType.label}</option>)}
              </Select2>
            </div>
            <div className="mb-4">
              <Label>Timeline Axis</Label>
              <Select2 {...register('timeLineAxis')}>
                <option value="">Select an option</option>
                {axis.map(axis => <option key={axis.value} value={axis.value}>{axis.label}</option>)}
              </Select2>
            </div>
          </DialogContent>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
            <Button.Primary type="submit">
              {pageState.mode === Modes.CREATE ? <><Icons.Add /> Add</> : 'Update'}
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Field</Title>
        <DialogContent>Are you sure you want to delete?</DialogContent>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
  </div>;
};

const fieldTypes = [
  { value: 'DATE', label: 'Date' },
  { value: 'NUMERIC', label: 'Numeric' },
  { value: 'TEXT', label: 'Text' },
  { value: 'OTHER', label: 'Other' }
];

const axis = [
  { value: 'X', label: 'X' },
  { value: 'Y', label: 'Y' }
];
